import { createFileRoute } from "@tanstack/react-router";
import { LegalList, LegalPage, LegalSection } from "@/components/LegalPage";

export const Route = createFileRoute("/privacidade")({
  head: () => ({
    meta: [
      { title: "Política de Privacidade — Projeto 101" },
      {
        name: "description",
        content:
          "Como o Projeto 101 coleta, utiliza, compartilha e protege dados pessoais no produto 101 Negócios Reais, em conformidade com a LGPD.",
      },
      { property: "og:title", content: "Política de Privacidade — Projeto 101" },
      { property: "og:description", content: "Tratamento de dados pessoais no Projeto 101 conforme a LGPD." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
  component: PrivacidadePage,
});

function PrivacidadePage() {
  return (
    <LegalPage
      title="Política de Privacidade"
      updatedAt="22 de julho de 2026"
      intro="Sua privacidade é importante para nós. Esta Política explica como tratamos seus dados pessoais em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018)."
    >
      <LegalSection n={1} title="Dados coletados">
        <p>Podemos coletar:</p>
        <LegalList
          items={[
            "Nome;",
            "CPF;",
            "E-mail;",
            "Telefone;",
            "Endereço IP;",
            "Informações do navegador;",
            "Dados de acesso;",
            "Dados referentes ao pagamento.",
          ]}
        />
        <p>Não armazenamos dados completos do cartão de crédito.</p>
      </LegalSection>

      <LegalSection n={2} title="Finalidade">
        <p>Utilizamos seus dados para:</p>
        <LegalList
          items={[
            "processar pagamentos;",
            "liberar acesso ao produto;",
            "autenticar usuários;",
            "enviar comunicações relacionadas à compra;",
            "prestar suporte;",
            "cumprir obrigações legais;",
            "prevenir fraudes.",
          ]}
        />
      </LegalSection>

      <LegalSection n={3} title="Pagamentos">
        <p>Os pagamentos são processados por provedores especializados.</p>
        <p>As informações financeiras são tratadas diretamente pelo gateway de pagamento.</p>
        <p>O Projeto 101 não armazena números completos de cartões.</p>
      </LegalSection>

      <LegalSection n={4} title="Compartilhamento">
        <p>Seus dados poderão ser compartilhados apenas quando necessário com:</p>
        <LegalList
          items={[
            "provedores de pagamento;",
            "provedores de hospedagem;",
            "serviços de autenticação;",
            "provedor de observabilidade (APM) para diagnóstico de erros e latência, com retenção máxima de 30 dias, contrato de tratamento (DPA) assinado e escrubbing de PII antes do envio;",
            "autoridades públicas quando exigido por lei.",
          ]}
        />
        <p>Jamais comercializamos seus dados.</p>
      </LegalSection>

      <LegalSection n={4.1} title="Observabilidade e diagnóstico (APM)">
        <p>
          Para manter a Plataforma 101 estável e detectar falhas com rapidez,
          utilizamos um provedor de observabilidade (APM). Os dados enviados
          passam por um processo automatizado de remoção de informações
          pessoais (e-mail, CPF, telefone, tokens, cookies, dados de cartão e
          mensagens do Consultor IA) antes de sair da nossa infraestrutura.
        </p>
        <LegalList
          items={[
            "Finalidade: diagnóstico de erros, latência e alertas operacionais;",
            "Base legal: legítimo interesse na segurança e continuidade do serviço (LGPD, art. 7º, IX);",
            "Retenção máxima: 30 dias no provedor;",
            "Kill-switch: coleta pode ser desligada em segundos sem publicar nova versão do site;",
            "Contrato: DPA (Data Processing Agreement) assinado com o processador antes de qualquer ativação em produção.",
          ]}
        />
        <p>
          Você pode solicitar a exclusão antecipada de traces vinculados à sua
          conta pelo canal de contato indicado na seção 10.
        </p>
      </LegalSection>

      <LegalSection n={5} title="Segurança">
        <p>Adotamos medidas técnicas e administrativas para proteger as informações contra:</p>
        <LegalList
          items={[
            "acesso não autorizado;",
            "alteração;",
            "divulgação;",
            "destruição;",
            "perda de dados.",
          ]}
        />
      </LegalSection>

      <LegalSection n={6} title="Cookies">
        <p>Utilizamos cookies para:</p>
        <LegalList
          items={[
            "autenticação;",
            "segurança;",
            "análise de navegação;",
            "melhoria da experiência do usuário.",
          ]}
        />
        <p>
          Você pode desativá-los no navegador, porém algumas funcionalidades poderão deixar de
          funcionar corretamente.
        </p>
      </LegalSection>

      <LegalSection n={7} title="Direitos do titular">
        <p>Você poderá solicitar:</p>
        <LegalList
          items={[
            "confirmação do tratamento;",
            "acesso aos dados;",
            "correção;",
            "atualização;",
            "anonimização;",
            "exclusão quando aplicável;",
            "portabilidade;",
            "revogação do consentimento.",
          ]}
        />
        <p>
          Solicitações:{" "}
          <a href="mailto:suporte@101negocios.com.br" className="underline text-[#1E7A5C]">
            suporte@101negocios.com.br
          </a>
        </p>
      </LegalSection>

      <LegalSection n={8} title="Retenção">
        <p>Os dados serão armazenados apenas pelo tempo necessário para:</p>
        <LegalList
          items={[
            "cumprir obrigações legais;",
            "executar o contrato;",
            "atender interesses legítimos.",
          ]}
        />
      </LegalSection>

      <LegalSection n={9} title="Alterações">
        <p>Esta Política poderá ser atualizada periodicamente.</p>
        <p>A versão vigente permanecerá disponível nesta página.</p>
      </LegalSection>

      <LegalSection n={10} title="Contato">
        <p>Projeto 101</p>
        <p>
          E-mail:{" "}
          <a href="mailto:suporte@101negocios.com.br" className="underline text-[#1E7A5C]">
            suporte@101negocios.com.br
          </a>
        </p>
      </LegalSection>
    </LegalPage>
  );
}
